/**
 * 認證服務
 * 處理登入、登出、權限檢查等功能
 */

import { authApi } from '../../api/auth.js';
import { accountHasPermission } from './permissionCheck.js';

/**
 * 提取錯誤訊息
 * @param {Error} error - 錯誤物件
 * @returns {string} 錯誤訊息
 */
function extractErrorMessage(error) {
  // 優先從 error.data.message 提取錯誤訊息（後端返回的格式）
  if (error.data && error.data.message) {
    return error.data.message;
  }
  
  // 其次從 error.response.data.message 提取
  if (error.response && error.response.data && error.response.data.message) {
    return error.response.data.message;
  }
  
  // 如果 error.message 是 axios 的默認錯誤訊息（如 "Request failed with status code 401"），則不使用
  if (error.message && !error.message.includes('Request failed with status code')) {
    return error.message;
  }
  
  // 默認錯誤訊息
  return '登入失敗，請檢查帳號密碼';
}

class AuthService {
  /**
   * 系統帳號登入
   * @param {string} username - 使用者名稱
   * @param {string} password - 密碼
   * @param {string} identityMode - 身份模式 (system/platform/client)
   * @returns {Promise<Object>} 登入結果
   */
  async systemLogin(username, password, identityMode = 'system') {
    try {
      const response = await authApi.systemLogin({
        username,
        password,
        identityMode,
      });

      // axios interceptor 已經返回 response.data，所以這裡直接是後端的回應格式
      if (response && response.success && response.data) {
        const { token, account } = response.data;
        
        // 儲存 token 和帳號資訊
        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_user', JSON.stringify(account));
        localStorage.setItem('auth_account_type', account.accountType || 'system');
        
        return {
          success: true,
          account,
          token,
        };
      }

      // 處理錯誤回應
      const errorMsg = (response && response.message) || (response && response.error && response.error.message) || '登入失敗';
      return {
        success: false,
        error: errorMsg,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || '登入失敗，請檢查帳號密碼',
      };
    }
  }

  /**
   * 平台帳號登入
   * @param {string} platformCode - 平台代碼
   * @param {string} username - 使用者名稱
   * @param {string} password - 密碼
   * @returns {Promise<Object>} 登入結果
   */
  async platformLogin(platformCode, username, password) {
    try {
      const response = await authApi.platformLogin({
        platformCode,
        username,
        password,
      });

      // axios interceptor 已經返回 response.data，所以這裡直接是後端的回應格式
      if (response && response.success && response.data) {
        const { token, account } = response.data;
        
        // 儲存 token 和帳號資訊
        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_user', JSON.stringify(account));
        localStorage.setItem('auth_account_type', account.accountType || 'platform');
        
        return {
          success: true,
          account,
          token,
        };
      }

      // 處理錯誤回應
      const errorMsg = (response && response.message) || (response && response.error && response.error.message) || '登入失敗';
      return {
        success: false,
        error: errorMsg,
      };
    } catch (error) {
      console.error('Platform login error:', {
        error,
        errorData: error.data,
        errorResponse: error.response,
        errorResponseData: error.response?.data,
        errorMessage: error.message,
        errorStack: error.stack
      });
      
      const errorMsg = extractErrorMessage(error);
      console.log('Extracted error message:', errorMsg);
      
      return {
        success: false,
        error: errorMsg,
      };
    }
  }

  /**
   * 客戶帳號登入
   * @param {string} clientCode - 客戶代碼
   * @param {string} username - 使用者名稱
   * @param {string} password - 密碼
   * @returns {Promise<Object>} 登入結果
   */
  async clientLogin(clientCode, username, password) {
    try {
      const response = await authApi.clientLogin({
        clientCode,
        username,
        password,
      });

      // axios interceptor 已經返回 response.data，所以這裡直接是後端的回應格式
      if (response && response.success && response.data) {
        const { token, account } = response.data;
        
        // 儲存 token 和帳號資訊
        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_user', JSON.stringify(account));
        localStorage.setItem('auth_account_type', account.accountType || 'client');
        
        return {
          success: true,
          account,
          token,
        };
      }

      // 處理錯誤回應
      const errorMsg = (response && response.message) || (response && response.error && response.error.message) || '登入失敗';
      return {
        success: false,
        error: errorMsg,
      };
    } catch (error) {
      console.error('[AuthService] clientLogin 發生錯誤:', error);
      return {
        success: false,
        error: error.message || '登入失敗，請檢查帳號密碼',
      };
    }
  }

  /**
   * 登出
   */
  logout() {
    authApi.logout();
  }

  /**
   * 取得當前使用者資訊
   * @returns {Object|null} 使用者資訊
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('auth_user');
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch (error) {
      return null;
    }
  }

  /**
   * 取得當前 Token
   * @returns {string|null} Token
   */
  getToken() {
    return localStorage.getItem('auth_token');
  }

  /**
   * 取得帳號類型
   * @returns {string|null} 帳號類型
   */
  getAccountType() {
    return localStorage.getItem('auth_account_type');
  }

  /**
   * 取得角色代碼（對應 accounts.role / JWT role）
   */
  getRole() {
    const user = this.getCurrentUser();
    return user?.role || null;
  }

  /**
   * 檢查是否已登入
   * @returns {boolean} 是否已登入
   */
  isAuthenticated() {
    return !!this.getToken() && !!this.getCurrentUser();
  }

  /**
   * 檢查是否有特定權限
   * @param {string} permission - 權限代碼
   * @returns {boolean} 是否有權限
   */
  hasPermission(permission) {
    return accountHasPermission(this.getCurrentUser(), permission);
  }
}

// 匯出單例
export default new AuthService();
